import * as React from 'react';
import Image from 'next/image';

import { cn } from '@/components/atoms/primitives/utils';
import { NATIONAL_LETTERMARK_SRC } from '@/components/atoms/LociLettermark/LociLettermark';

/**
 * Public-file path for the brand-amber dot cluster (DES-85 Option B,
 * 2026-09-27). The dots are theme-invariant — always `#FFB800`, never a
 * dark-step substitute (a symbol, not text, so its lower on-white presence
 * is acceptable) — so unlike the lettermark they carry no `--nav-*` token
 * and are never masked/recolored; this stays a plain `<img>`.
 *
 * This is `loci-symbol.svg` (viewBox `0 0 105.51 106.49`), copied verbatim
 * from the canonical LOCI Design Studio asset. Since WEB-1015,
 * `apps/web/public/brand/loci-dots.svg` draws the identical cluster (same
 * viewBox, same four circles, same `#FFB800` fill); the retired cluster
 * geometry is gone from every web brand file, and
 * `components/foundation/__tests__/BrandAssets.test.ts` pins that.
 * `loci-symbol.svg` stays the path the nav lockup loads, so the nav never
 * moves when the gallery assets are re-exported.
 */
export const NATIONAL_DOTS_SRC = '/brand/loci-symbol.svg';

/**
 * Intrinsic size of {@link NATIONAL_DOTS_SRC}'s viewBox, passed to
 * `next/image` so the rendered aspect ratio (~14.9×15 at the lockup's fixed
 * 15px height) is exact — avoids CLS from an approximated intrinsic ratio.
 */
const DOTS_INTRINSIC_WIDTH = 105.51;
const DOTS_INTRINSIC_HEIGHT = 106.49;

export interface LociDotsMarkProps {
  className?: string;
  'data-testid'?: string;
}

/**
 * LociDotsMark — the four-circle brand-amber dot cluster that precedes the
 * lettermark in the v2 global-nav lockup (FRE-1361 / DES-85). Always brand
 * amber regardless of theme; decorative — the containing lockup carries the
 * accessible name (a home link's `aria-label`, or a menu lockup's named
 * {@link LociLettermarkMask}).
 */
export function LociDotsMark({
  className,
  'data-testid': testId = 'loci-dots-mark',
}: LociDotsMarkProps): React.ReactElement {
  return (
    <Image
      src={NATIONAL_DOTS_SRC}
      alt=""
      aria-hidden="true"
      width={DOTS_INTRINSIC_WIDTH}
      height={DOTS_INTRINSIC_HEIGHT}
      priority
      data-testid={testId}
      // `.loci-dots-mark` (styles/components/nav-lockup.css, `@layer components`) carries the
      // 15px height — a caller's height/size utility still wins over it.
      className={cn('loci-dots-mark w-auto shrink-0', className)}
    />
  );
}

export interface LociLettermarkMaskProps {
  className?: string;
  'data-testid'?: string;
  /**
   * Accessible name. When set, the span is `role="img"` with that label —
   * use this when the mark is not already inside a named link (e.g. the
   * NavMenuSheet chrome-row lockup). Omit to keep the default decorative
   * `aria-hidden` (the city-header brand link supplies the name instead).
   */
  alt?: string;
}

/**
 * LociLettermarkMask — the national "LOCI" wordmark as a CSS-masked `<span>`
 * (FRE-1361 / DES-85 Option B, the "v2 lockup" rule, 2026-09-27).
 *
 * Painted with `background-color: var(--nav-lettermark)` and masked to the
 * lettermark artwork, so one markup themes itself: dark chrome → cream
 * `#F4F1E8`, light chrome → ink `#1E2B4B` (`--nav-fg`). Never amber — the
 * {@link LociDotsMark} beside it carries the constant brand amber instead,
 * which is why `--nav-lettermark` no longer resolves to `--loci-amber` /
 * `--loci-amber-dark` (see `styles/tokens/color.css`).
 *
 * This is the "masked span" implementation the design system prefers over
 * an `<img>` swap between `loci-lettermark-cream.svg` / `-ink.svg` — no
 * second asset, no `filter: brightness()`. `LociLettermark` (this folder's
 * sibling, inline-SVG `currentColor`) is the pre-existing alternative and
 * stays in place where it already ships (`AppHeader`, `NavMenuLockup`'s
 * non-national fallback) — both read the same `--nav-lettermark` token, so
 * they theme identically without code changes.
 */
export function LociLettermarkMask({
  className,
  'data-testid': testId = 'loci-lettermark-mask',
  alt,
}: LociLettermarkMaskProps): React.ReactElement {
  const named = Boolean(alt);
  const maskImage = `url(${NATIONAL_LETTERMARK_SRC})`;
  return (
    <span
      aria-hidden={named ? undefined : true}
      role={named ? 'img' : undefined}
      aria-label={named ? alt : undefined}
      data-testid={testId}
      // `.loci-lettermark-mask` (styles/components/nav-lockup.css, `@layer components`)
      // carries the static paint: 50.8px width, `--nav-lettermark` fill and the
      // standard + `-webkit-` mask repeat/position/size. Only the mask IMAGE
      // stays inline — it is derived from `NATIONAL_LETTERMARK_SRC`, so the
      // asset path has one source of truth.
      className={cn('loci-lettermark-mask inline-block h-4 shrink-0', className)}
      style={{
        WebkitMaskImage: maskImage,
        maskImage,
      }}
    />
  );
}
